import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

const today = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

const RefundPolicy = () => {
    return (
        <div className="theme-horizon" data-testid="refund-policy-page">
            <SEO
                title="Refund Policy · SouthTrip.in"
                description="Refund and cancellation policy for SouthTrip.in cab bookings, airport transfers and Kerala tour packages. Processing times and refund methods."
                path="/refund-policy"
            />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
                <header className="mb-10">
                    <div className="hz-pill">Legal</div>
                    <h1 className="font-hz-display text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
                        Refund Policy
                    </h1>
                    <p className="text-[var(--ink-soft)] mt-3 text-sm">Last updated: {today}</p>
                </header>

                <section className="space-y-6 text-[var(--ink-soft)] leading-relaxed text-[15px] md:text-base">
                    <p>
                        SouthTrip.in operates a <strong>pay-direct-to-driver</strong> model for cab bookings and
                        airport transfers, which means no advance payment is collected and therefore no standard
                        refund scenario applies. This policy details how cancellations and refunds work for our tour
                        packages and any prepaid services.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">1. Cab bookings &amp; airport transfers</h2>
                    <p>
                        Because payment is made directly to the driver at the end of the trip, no refund is required
                        for cancellations. However, cancellation charges may apply if the booking is cancelled within
                        24 hours of pickup (see our <Link to="/terms" className="text-[var(--brand)] underline">Terms of Service</Link>).
                        Where a charge is due, it will be invoiced separately.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">2. Tour packages — cancellation schedule</h2>
                    <table className="w-full text-sm border border-[var(--border)] rounded-xl overflow-hidden mt-3">
                        <thead className="bg-[var(--bg-surface)] text-[var(--ink)]">
                            <tr>
                                <th className="text-left p-3">Cancellation notice (before departure)</th>
                                <th className="text-left p-3">Refund</th>
                            </tr>
                        </thead>
                        <tbody className="text-[var(--ink-soft)]">
                            <tr className="border-t border-[var(--border)]">
                                <td className="p-3">More than 48 hours</td>
                                <td className="p-3 text-[var(--ink)] font-semibold">100 % refund</td>
                            </tr>
                            <tr className="border-t border-[var(--border)]">
                                <td className="p-3">24 – 48 hours</td>
                                <td className="p-3 text-[var(--ink)] font-semibold">50 % refund</td>
                            </tr>
                            <tr className="border-t border-[var(--border)]">
                                <td className="p-3">Less than 24 hours / no-show</td>
                                <td className="p-3 text-[var(--ink)] font-semibold">No refund</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="text-xs text-[var(--ink-softer)]">
                        Where a third-party booking has been made on your behalf (hotel, houseboat, train ticket,
                        domestic flight), the third party&apos;s own cancellation terms apply to that portion of the
                        booking.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">3. How to request a refund</h2>
                    <ol className="list-decimal pl-6 space-y-1">
                        <li>Send an email to <a href="mailto:info@southtrip.in" className="text-[var(--brand)] underline">info@southtrip.in</a> with your booking reference (e.g. <code>bkg-xxxxxxxx</code>) and the reason for cancellation.</li>
                        <li>Our team will acknowledge your request within 24 hours.</li>
                        <li>Once approved, the refund will be processed to the original payment method.</li>
                    </ol>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">4. Refund processing time</h2>
                    <p>
                        Approved refunds are processed within <strong>7 working days</strong> from the date of
                        approval. The amount may take an additional 2 – 5 banking days to reflect in your account,
                        depending on your bank or card issuer.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">5. Service changes initiated by SouthTrip</h2>
                    <p>
                        If SouthTrip is unable to deliver a confirmed service due to circumstances within our control
                        (vehicle breakdown without replacement, driver unavailability, etc.), you are entitled to a
                        <strong> 100 % refund</strong> of the affected portion, regardless of the cancellation
                        schedule above. We will also make every effort to provide an equivalent alternative.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">6. Force majeure</h2>
                    <p>
                        In the event of cancellation due to force majeure (severe weather, natural disaster, strikes,
                        political unrest, pandemic-related restrictions), SouthTrip will offer either a full credit
                        valid for 12 months OR a refund minus any non-recoverable third-party costs already incurred
                        on your behalf.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">7. Contact</h2>
                    <p>
                        <strong>SouthTrip.in by LuxSaCabs</strong><br />
                        35 P/N, Kavarapparambu Road, Angamaly, Kerala 683572, India<br />
                        Email: <a href="mailto:info@southtrip.in" className="text-[var(--brand)] underline">info@southtrip.in</a><br />
                        Phone: <a href="tel:+918075814740" className="text-[var(--brand)] underline">+91 80-75814740</a>
                    </p>

                    <p className="text-xs text-[var(--ink-softer)] pt-8 border-t border-[var(--border)] mt-12">
                        See also: <Link to="/privacy-policy" className="hz-link">Privacy Policy</Link>{" · "}
                        <Link to="/terms" className="hz-link">Terms of Service</Link>
                    </p>
                </section>
            </article>
        </div>
    );
};

export default RefundPolicy;
